import { useState } from "react";
import axios from "axios";
import { Star, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

function StarRating({ value, onChange, readOnly = false, size = "h-4 w-4" }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={readOnly ? "star-readonly" : "star-input"}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          className={readOnly ? "cursor-default" : "cursor-pointer"}
        >
          <Star className={`${size} ${n <= value ? "fill-[#39FF14] text-[#39FF14]" : "text-[#1A1A1A]"}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

export default function ProductReviews({ productId, user, summary, onChange }) {
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/api/products/${productId}/reviews`, { rating, title: title.trim(), body: body.trim() });
      setTitle(""); setBody(""); setRating(5);
      setShowForm(false);
      onChange?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to post review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMine = async (reviewId) => {
    if (!window.confirm("Delete your review?")) return;
    try {
      await axios.delete(`${API}/api/reviews/${reviewId}`);
      onChange?.();
    } catch (e) {
      setError("Could not delete review");
    }
  };

  if (!summary) return null;

  return (
    <div className="border-t border-[#1A1A1A] mt-12 pt-12" data-testid="reviews-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14] mb-2">Reviews</p>
          <div className="flex items-center gap-3">
            <StarRating value={Math.round(summary.average)} readOnly size="h-5 w-5" />
            <span className="font-mono text-sm text-[#F2F2F2]">
              {summary.count > 0 ? `${summary.average.toFixed(1)} / 5` : "No ratings yet"}
            </span>
            {summary.count > 0 && <span className="font-mono text-xs text-[#A1A1A1]">({summary.count} review{summary.count !== 1 ? "s" : ""})</span>}
          </div>
        </div>
        {user && summary.can_review && !showForm && (
          <Button
            onClick={() => setShowForm(true)}
            data-testid="write-review-btn"
            className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider"
          >
            Write a Review
          </Button>
        )}
      </div>

      {/* Eligibility hint */}
      {user && !summary.can_review && !summary.has_reviewed && (
        <p className="text-[#A1A1A1] text-xs font-mono mb-6" data-testid="review-eligibility-hint">
          Only customers who have purchased this product can leave a review.
        </p>
      )}
      {!user && (
        <p className="text-[#A1A1A1] text-xs font-mono mb-6">
          Sign in to leave a review (must have purchased this product).
        </p>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#0D0D0D] border border-[#1A1A1A] p-5 mb-6 space-y-4" data-testid="review-form">
          <div>
            <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Rating</Label>
            <div className="mt-2"><StarRating value={rating} onChange={setRating} size="h-6 w-6" /></div>
          </div>
          <div>
            <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Title (optional)</Label>
            <Input
              data-testid="review-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Solid mod, fast shipping..."
              className="bg-[#050505] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono"
            />
          </div>
          <div>
            <Label className="text-[#A1A1A1] font-mono text-[10px] uppercase tracking-wider">Review</Label>
            <textarea
              data-testid="review-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              rows={5}
              placeholder="Tell other shoppers about your experience..."
              className="w-full mt-1 bg-[#050505] border border-[#1A1A1A] text-[#F2F2F2] focus:border-[#39FF14] outline-none p-3 font-mono text-sm"
            />
          </div>
          {error && <p className="text-red-400 text-xs font-mono" data-testid="review-error">{error}</p>}
          <div className="flex gap-3">
            <Button type="submit" disabled={submitting} data-testid="review-submit" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono text-xs uppercase tracking-wider px-6">
              {submitting ? "Posting..." : "Post Review"}
            </Button>
            <Button type="button" onClick={() => setShowForm(false)} variant="outline" className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {summary.reviews.length === 0 ? (
        <p className="text-[#A1A1A1] text-sm font-mono">Be the first to review.</p>
      ) : (
        <div className="space-y-5" data-testid="reviews-list">
          {summary.reviews.map(r => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-[#0D0D0D] border border-[#1A1A1A] p-5">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex items-center gap-3">
                  <StarRating value={r.rating} readOnly />
                  {r.title && <span className="font-bold text-[#F2F2F2]">{r.title}</span>}
                </div>
                {user && user.id === r.user_id && (
                  <button onClick={() => handleDeleteMine(r.id)} title="Delete your review" className="text-[#A1A1A1] hover:text-red-400" data-testid={`delete-review-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {r.body && <p className="text-[#F2F2F2] text-sm whitespace-pre-wrap mb-2">{r.body}</p>}
              <p className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">
                {r.user_name || "Anonymous"} · {new Date(r.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
